import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SearchHeader = ({ 
  searchQuery, 
  resultCount, 
  filters, 
  onClearSearch,
  onToggleFilters,
  showMobileFilters 
}) => {
  const getActiveFilterCount = () => {
    let count = 0;
    if (filters?.category && filters?.category !== 'all') count++;
    if (filters?.location) count++;
    if (filters?.dateFrom || filters?.dateTo) count++;
    if (filters?.priceRange && filters?.priceRange !== 'all') count++;
    if (filters?.deliveryType && filters?.deliveryType !== 'all') count++;
    if (filters?.ukWide) count++;
    return count;
  };

  const getSearchSummary = () => {
    const parts = [];
    
    if (searchQuery) {
      parts?.push(`"${searchQuery}"`);
    }
    
    if (filters?.category && filters?.category !== 'all') {
      const categoryLabels = {
        'mindfulness-meditation': 'Mindfulness & Meditation',
        'yoga-movement': 'Yoga & Movement',
        'energy-healing': 'Energy Healing',
        'complementary-therapies': 'Complementary Therapies',
        'nutrition-wellness': 'Nutrition & Wellness',
        'spirituality-esoterica': 'Spirituality & Esoterica',
        'sound-breathwork': 'Sound & Breathwork',
        'coaching-development': 'Coaching & Development',
        'creative-therapy': 'Creative Therapy',
        'retreats-immersions': 'Retreats & Immersions',
        'nature-eco-therapy': 'Nature & Eco Therapy',
        'ceremonies-community': 'Ceremonies & Community',
        'training-professional': 'Training & Professional',
        'courses': 'Courses',
        'circles': 'Circles'
      };
      parts?.push(`in ${categoryLabels?.[filters?.category]}`);
    }
    
    if (filters?.location) {
      parts?.push(`near ${filters?.location}`);
    }
    
    return parts?.join(' ');
  };

  const activeFilterCount = getActiveFilterCount();
  const searchSummary = getSearchSummary();

  return (
    <div className="bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search Summary */}
        <div className="mb-4">
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold text-foreground mb-2">
            {searchSummary || 'All Listings'}
          </h1>
          <p className="text-muted-foreground">
            {resultCount} {resultCount === 1 ? 'result' : 'results'} found
            {filters?.ukWide && ' across the UK'}
          </p>
        </div>

        {/* Active Filters & Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Active Filters */}
          <div className="flex flex-wrap items-center gap-2">
            {searchQuery && (
              <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                <Icon name="Search" size={14} className="mr-1" />
                <span>"{searchQuery}"</span>
                <button
                  onClick={onClearSearch}
                  className="ml-2 hover:bg-primary/20 rounded-full p-0.5 transition-gentle"
                >
                  <Icon name="X" size={12} />
                </button>
              </div>
            )}
            
            {filters?.category && filters?.category !== 'all' && (
              <div className="flex items-center bg-secondary/20 text-secondary-foreground px-3 py-1 rounded-full text-sm">
                <Icon name="Tag" size={14} className="mr-1" />
                <span>{filters?.category?.replace('-', ' ')}</span>
              </div>
            )}
            
            {filters?.location && (
              <div className="flex items-center bg-accent/20 text-accent-foreground px-3 py-1 rounded-full text-sm">
                <Icon name="MapPin" size={14} className="mr-1" />
                <span>{filters?.location}</span>
              </div>
            )}
            
            {(filters?.dateFrom || filters?.dateTo) && (
              <div className="flex items-center bg-muted text-muted-foreground px-3 py-1 rounded-full text-sm">
                <Icon name="Calendar" size={14} className="mr-1" />
                <span>
                  {filters?.dateFrom && filters?.dateTo 
                    ? `${new Date(filters.dateFrom)?.toLocaleDateString('en-GB')} - ${new Date(filters.dateTo)?.toLocaleDateString('en-GB')}`
                    : filters?.dateFrom 
                      ? `From ${new Date(filters.dateFrom)?.toLocaleDateString('en-GB')}`
                      : `Until ${new Date(filters.dateTo)?.toLocaleDateString('en-GB')}`
                  }
                </span>
              </div>
            )}
            
            {filters?.priceRange && filters?.priceRange !== 'all' && (
              <div className="flex items-center bg-warning/20 text-warning-foreground px-3 py-1 rounded-full text-sm">
                <Icon name="PoundSterling" size={14} className="mr-1" />
                <span>{filters?.priceRange === '200+' ? 'Over £200' : `£${filters?.priceRange?.replace('-', ' - £')}`}</span>
              </div>
            )}
            
            {filters?.deliveryType && filters?.deliveryType !== 'all' && (
              <div className="flex items-center bg-success/20 text-success-foreground px-3 py-1 rounded-full text-sm">
                <Icon name={filters?.deliveryType === 'online' ? 'Video' : 'MapPin'} size={14} className="mr-1" />
                <span>{filters?.deliveryType === 'online' ? 'Online' : 'In-person'}</span>
              </div>
            )}
            
            {filters?.ukWide && (
              <div className="flex items-center bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                <Icon name="Globe" size={14} className="mr-1" />
                <span>UK-wide</span>
              </div>
            )}
          </div>

          {/* Mobile Filter Toggle */}
          <div className="lg:hidden">
            <Button
              variant="outline"
              onClick={onToggleFilters}
              iconName="Filter"
              iconPosition="left"
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="ml-2 bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        {(searchQuery || activeFilterCount > 0) && (
          <div className="mt-4 pt-4 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onClearSearch();
                // This would trigger a filter reset in the parent component
              }}
              iconName="RotateCcw"
              iconPosition="left"
            >
              Clear all filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchHeader;